import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { callDocumentDrafter, getErrorMessage } from '@/utils/ai'
import { data as apiData } from '@/utils/auth'
import MarkdownText from '@/components/common/MarkdownText'

// ── Document templates ────────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: 'parenting-plan',
    emoji: '📋',
    title: 'Parenting Plan',
    desc: '50/50 or custom custody schedule template.',
    tag: 'Popular',
    tagColor: 'bg-brand-100 text-brand-700',
  },
  {
    id: 'medical-consent',
    emoji: '🏥',
    title: 'Medical Consent Form',
    desc: 'Authorize medical treatment for your child.',
    tag: null,
  },
  {
    id: 'cps-response',
    emoji: '🛡️',
    title: 'CPS Response Letter',
    desc: 'Assert your parental rights in writing.',
    tag: null,
  },
  {
    id: 'custody-mod',
    emoji: '⚖️',
    title: 'Custody Modification',
    desc: 'Petition to modify an existing custody order.',
    tag: null,
  },
  {
    id: 'court-declaration',
    emoji: '📜',
    title: 'Court Declaration',
    desc: 'Statement of facts for court submission.',
    tag: null,
  },
  {
    id: 'temp-custody',
    emoji: '🤝',
    title: 'Temporary Custody Agreement',
    desc: 'Short-term custody arrangement between parents.',
    tag: null,
  },
  {
    id: 'child-support',
    emoji: '💰',
    title: 'Child Support Agreement',
    desc: 'Draft a child support agreement based on your calculated figures.',
    tag: null,
  },
  {
    id: 'contempt-motion',
    emoji: '⚖️',
    title: 'Motion for Contempt',
    desc: 'File a contempt motion against a co-parent who violated a court order.',
    tag: 'High Impact',
    tagColor: 'bg-red-100 text-red-700',
  },
]

// ── Upload states ─────────────────────────────────────────────────────────────
const FILE_TYPES = '.pdf,.doc,.docx,.jpg,.jpeg,.png'

// ── Uploaded file row ─────────────────────────────────────────────────────────
function FileRow({ file, onRemove }) {
  const ext = file.name.split('.').pop().toLowerCase()
  const icon = ['pdf'].includes(ext) ? '📄' : ['jpg','jpeg','png'].includes(ext) ? '🖼️' : '📝'
  const size = file.size < 1024 * 1024
    ? `${(file.size / 1024).toFixed(0)} KB`
    : `${(file.size / (1024 * 1024)).toFixed(1)} MB`

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
      <span className="text-xl flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-700 truncate">{file.name}</p>
        <p className="text-sm text-slate-500">{size}</p>
      </div>
      <button
        onClick={() => onRemove(file.name)}
        className="flex-shrink-0 text-slate-500 hover:text-red-500 tap-target transition-colors"
        aria-label="Remove file"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

// ── Copy icon ─────────────────────────────────────────────────────────────────
function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
    </svg>
  )
}

// ── Document viewer (full-screen overlay) ─────────────────────────────────────
function DocumentViewer({ template, docText, onClose }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(docText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard unavailable */ }
  }

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=900,height=700')
    if (!win) { alert('Pop-up blocked. Please allow pop-ups for this site.'); return }
    win.document.write(`<!DOCTYPE html><html><head><title>${template.title} — GoodGuardian</title>
<style>
  body{font-family:Arial,sans-serif;font-size:11pt;line-height:1.6;color:#1e293b;max-width:780px;margin:40px auto;padding:0 24px}
  h1{color:#1F497D;font-size:16pt;border-bottom:2px solid #1F497D;padding-bottom:8px;margin-bottom:20px}
  pre{white-space:pre-wrap;font-family:Arial,sans-serif;font-size:10.5pt}
  .bar{background:#1F497D;color:#fff;padding:10px 16px;display:flex;justify-content:space-between;align-items:center;margin:-40px -24px 30px;position:sticky;top:0}
  .bar button{background:#fff;color:#1F497D;border:none;padding:5px 14px;border-radius:20px;font-weight:bold;cursor:pointer}
  @media print{.bar{display:none}@page{margin:.75in}}
</style></head><body>
<div class="bar"><span>🛡️ GoodGuardian — ${template.title}</span><button onclick="window.print()">🖨️ Print / Save as PDF</button></div>
<pre>${docText.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>
</body></html>`)
    win.document.close()
    win.focus()
  }

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col" role="dialog" aria-modal="true" aria-label={`${template.title} document viewer`}>
      {/* Header bar */}
      <div className="brand-gradient flex items-center gap-3 px-4 py-3 flex-shrink-0">
        <button onClick={onClose} className="text-white/80 hover:text-white tap-target" aria-label="Close document viewer">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm truncate">{template.title}</p>
          <p className="text-blue-100 text-xs">Review before filing — attorney approval required</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleCopy}
            className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-full font-medium transition-colors">
            <CopyIcon />
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button onClick={handlePrint}
            className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-full font-medium transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        </div>
      </div>

      {/* Document text */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50">
        <div className="bg-white rounded-xl shadow-card p-4 text-sm leading-relaxed text-slate-800">
          <MarkdownText content={docText} hideAttorneyButton={true} />
        </div>
      </div>

      {/* Footer disclaimer */}
      <div className="disclaimer mx-4 my-3 flex-shrink-0">
        ⚠️ Fill in all <strong>[PLACEHOLDER]</strong> fields. Have a licensed attorney review every
        ⚠️-flagged section before filing with any court.
      </div>
    </div>
  )
}

// ── Template generate modal ────────────────────────────────────────────────────
function GenerateModal({ template, state, onClose, onGenerate, initialNotes }) {
  const [notes, setNotes] = useState(initialNotes || '')
  const [loading, setLoading] = useState(false)
  const [error, setError]  = useState(null)
  const [docText, setDocText] = useState(null) // generated document

  const handleGenerate = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await callDocumentDrafter({
        document_type: template.id,
        state,
        context: `Document: ${template.title}. ${template.desc}`,
        details: notes || 'No additional details provided.',
      })
      // Persist to backend first, then show viewer
      await onGenerate(template, result.document)
      setDocText(result.document)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  // Show full-screen document viewer once generated
  if (docText) {
    return <DocumentViewer template={template} docText={docText} onClose={onClose} />
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center lg:justify-center z-50" onClick={onClose}>
      <div className="w-full max-w-lg mx-auto bg-white rounded-t-3xl lg:rounded-3xl p-6 flex flex-col gap-4 animate-slide-up max-h-[90vh] lg:max-h-[85vh] overflow-y-auto"
        role="dialog" aria-modal="true" aria-label={`Generate ${template.title}`}
        onClick={e => e.stopPropagation()}>
        {/* Handle bar */}
        <div className="w-12 h-1 rounded-full bg-slate-200 mx-auto -mt-2 mb-2" />

        <div className="flex items-center gap-3">
          <span className="text-2xl">{template.emoji}</span>
          <div>
            <h3 className="font-bold text-slate-800">{template.title}</h3>
            {state && <p className="text-sm text-slate-500">Tailored for {state}</p>}
          </div>
        </div>

        <p className="text-base text-slate-600">{template.desc}</p>

        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-1">
            Additional context <span className="text-slate-500 font-normal">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="E.g. 50/50 split, children aged 4 and 7, in Broward County FL..."
            rows={3}
            className="input-base resize-none text-base"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="disclaimer">
          ⚠️ Generated documents require attorney review before filing or presenting to any court.
        </div>

        <div className="flex gap-3">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1" loading={loading} onClick={handleGenerate}>
            {loading ? 'Drafting…' : 'Generate Draft'}
          </Button>
        </div>
      </div>
    </div>
  )
}

const STATUS_OPTIONS = [
  'Draft — Needs Attorney Review',
  'Reviewed',
  'Filed',
  'Archived',
]

// ── Edit document modal ───────────────────────────────────────────────────────
function EditDocModal({ doc, onClose, onSave }) {
  const [title,   setTitle]   = useState(doc.title  || '')
  const [emoji,   setEmoji]   = useState(doc.emoji  || '📄')
  const [status,  setStatus]  = useState(doc.status || STATUS_OPTIONS[0])
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required.'); return }
    setLoading(true)
    setError(null)
    try {
      await apiData.putDoc(doc.id, { title: title.trim(), emoji: emoji.trim() || '📄', status })
      onSave(doc.id, { title: title.trim(), emoji: emoji.trim() || '📄', status })
      onClose()
    } catch (err) {
      setError(err?.message || 'Failed to save. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center lg:justify-center z-50" onClick={onClose}>
      <div
        className="w-full max-w-lg mx-auto bg-white rounded-t-3xl lg:rounded-3xl p-6 flex flex-col gap-4 animate-slide-up"
        role="dialog" aria-modal="true" aria-label="Edit document"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full bg-slate-200 mx-auto -mt-2 mb-1" />
        <h3 className="font-bold text-slate-800 text-base">Edit Document</h3>

        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-1">Title</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Document title"
            className="input-base text-base w-full"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-1">Emoji</label>
          <input
            value={emoji}
            onChange={e => setEmoji(e.target.value)}
            placeholder="📄"
            maxLength={4}
            className="input-base text-base w-24"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-1">Status</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="input-base text-base w-full"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="ghost"   size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1" loading={loading} onClick={handleSave}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Documents screen ──────────────────────────────────────────────────────────
export default function Documents() {
  const { user, documents, addDocument, updateDocument, files, uploadFile, renameFile } = useApp()
  const location     = useLocation()
  const fileInputRef = useRef(null)
  const [uploadedFiles, setUploadedFiles] = useState([])   // staging: File objects not yet uploaded
  const [isDragging, setIsDragging] = useState(false)
  const [activeModal, setActiveModal] = useState(null)     // template being generated
  const [activeNotes, setActiveNotes] = useState('')       // pre-filled context for modal
  const [tab, setTab] = useState('generate')               // 'generate' | 'upload' | 'my-docs'
  const [uploadLoading, setUploadLoading] = useState(false)

  // Auto-open a template when navigated from Child Support Calculator (or similar)
  useEffect(() => {
    const { autoTemplate, prefillContext } = location.state || {}
    if (autoTemplate) {
      const tmpl = TEMPLATES.find(t => t.id === autoTemplate)
      if (tmpl) {
        setActiveNotes(prefillContext || '')
        setActiveModal(tmpl)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [uploadError, setUploadError] = useState(null)
  const [viewDoc, setViewDoc] = useState(null)             // {template, docText} for viewer
  const [editDoc, setEditDoc] = useState(null)             // document being edited
  const [renameTarget, setRenameTarget] = useState(null)   // file object being renamed
  const [renameValue, setRenameValue]   = useState('')     // controlled input value
  const [renameLoading, setRenameLoading] = useState(false)
  const [renameError, setRenameError]   = useState(null)

  // ── File handling ──────────────────────────────────────────────────────────
  const handleFiles = (fileList) => {
    const valid = Array.from(fileList).filter(f => f.size <= 20 * 1024 * 1024) // 20MB max
    setUploadedFiles(prev => {
      const names = new Set(prev.map(f => f.name))
      return [...prev, ...valid.filter(f => !names.has(f.name))]
    })
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const removeFile = (name) => setUploadedFiles(prev => prev.filter(f => f.name !== name))

  // Persist generated document to D1 via AppContext, then show viewer
  const handleGenerated = async (template, docText) => {
    await addDocument({
      title: template.title,
      emoji: template.emoji,
      document_type: template.id,
      content: docText,
      status: 'Draft — Needs Attorney Review',
    })
    // Don't close modal — DocumentViewer is shown next inside GenerateModal
  }

  // Open a saved document from "My Docs"
  const handleOpenDoc = async (doc) => {
    if (doc.content) {
      // Content already available
      setViewDoc({ template: { title: doc.title, emoji: doc.emoji || '📄' }, docText: doc.content })
      return
    }
    // Fetch full content from API
    try {
      const d = await apiData.getDoc(doc.id)
      const content = d?.document?.content || d?.content || '(Content not available)'
      setViewDoc({ template: { title: doc.title, emoji: doc.emoji || '📄' }, docText: content })
    } catch {
      setViewDoc({ template: { title: doc.title, emoji: doc.emoji || '📄' }, docText: '(Content could not be loaded)' })
    }
  }

  // Download an uploaded file from R2
  const handleDownloadFile = async (f) => {
    try {
      const res = await apiData.getFile(f.id)
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = f.filename || 'document'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert('Could not download file. Please try again.')
    }
  }

  // Upload staged files to R2 via AppContext
  const saveUploadedFiles = async () => {
    if (!uploadedFiles.length) return
    setUploadLoading(true)
    setUploadError(null)
    try {
      for (const file of uploadedFiles) {
        await uploadFile(file)
      }
      setUploadedFiles([])
      setTab('my-docs')
    } catch {
      setUploadError('Some files failed to upload. Please try again.')
    } finally {
      setUploadLoading(false)
    }
  }

  // Open rename modal for an uploaded file
  const openRename = (e, f) => {
    e.stopPropagation()
    setRenameTarget(f)
    setRenameValue(f.filename || '')
    setRenameError(null)
  }

  const closeRename = () => {
    setRenameTarget(null)
    setRenameValue('')
    setRenameError(null)
    setRenameLoading(false)
  }

  const submitRename = async () => {
    const trimmed = renameValue.trim()
    if (!trimmed) { setRenameError('Filename cannot be empty.'); return }
    setRenameLoading(true)
    setRenameError(null)
    try {
      await renameFile(renameTarget.id, trimmed)
      closeRename()
    } catch {
      setRenameError('Could not rename file. Please try again.')
      setRenameLoading(false)
    }
  }

  const totalMyDocs = documents.length + files.length
  const tabs = [
    { id: 'generate', label: 'Generate' },
    { id: 'upload',   label: 'Upload' },
    { id: 'my-docs',  label: `My Docs${totalMyDocs > 0 ? ` (${totalMyDocs})` : ''}` },
  ]

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Documents" />

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div className="flex border-b border-slate-100 bg-white flex-shrink-0" role="tablist" aria-label="Documents sections">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            role="tab"
            aria-selected={tab === t.id}
            className={[
              'flex-1 py-3 text-sm font-semibold transition-colors duration-150',
              tab === t.id
                ? 'text-brand-700 border-b-2 border-brand-700'
                : 'text-slate-600 hover:text-slate-800',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="screen no-scrollbar px-4 sm:px-6 lg:px-8 py-5">
        <div className="mx-auto w-full max-w-6xl">

        {/* ── Generate tab ───────────────────────────────────────────────── */}
        {tab === 'generate' && (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-base font-bold text-slate-800">Document Templates</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Select a template to generate a customized draft
                {user?.state ? ` for ${user.state}` : ''}.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveModal(t)}
                  className="card h-full min-h-[120px] lg:min-h-[132px] flex items-center gap-4 text-left hover:shadow-card-hover active:scale-[0.98] transition-all duration-150"
                >
                  <span className="text-2xl flex-shrink-0">{t.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800 text-base">{t.title}</p>
                      {t.tag && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${t.tagColor || 'bg-slate-100 text-slate-600'}`}>
                          {t.tag}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{t.desc}</p>
                  </div>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                    className="w-4 h-4 text-slate-300 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>

            <div className="disclaimer">
              ⚠️ All generated documents include <strong>[PLACEHOLDER]</strong> fields
              and ⚠️ attorney-review flags on legally sensitive sections. Never file
              without professional review.
            </div>
          </div>
        )}

        {/* ── Upload tab ─────────────────────────────────────────────────── */}
        {tab === 'upload' && (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-base font-bold text-slate-800">Upload Documents</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Upload existing documents — court orders, letters, forms — for safekeeping.
              </p>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={[
                'border-2 border-dashed rounded-2xl p-8 flex flex-col items-center gap-3 cursor-pointer transition-all duration-150',
                isDragging
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-slate-200 bg-slate-50 hover:border-brand-300 hover:bg-brand-50',
              ].join(' ')}
            >
              <div className="text-4xl">{isDragging ? '📂' : '📁'}</div>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-700">
                  {isDragging ? 'Drop files here' : 'Tap to upload files'}
                </p>
                <p className="text-sm text-slate-500 mt-1">PDF, Word, JPG, PNG · Max 20MB</p>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={FILE_TYPES}
              className="hidden"
              onChange={e => handleFiles(e.target.files)}
            />

            {/* Staged files list */}
            {uploadedFiles.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                  Ready to upload ({uploadedFiles.length})
                </p>
                {uploadedFiles.map(f => (
                  <FileRow key={f.name} file={f} onRemove={removeFile} />
                ))}
                {uploadError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                    {uploadError}
                  </div>
                )}
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={uploadLoading}
                  onClick={saveUploadedFiles}
                >
                  {uploadLoading ? 'Uploading…' : 'Save to My Docs'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── My Docs tab ────────────────────────────────────────────────── */}
        {tab === 'my-docs' && (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-base font-bold text-slate-800">My Documents</h2>
              <p className="text-sm text-slate-500 mt-0.5">Your generated and uploaded documents.</p>
            </div>

            {totalMyDocs === 0 ? (
              <div className="flex flex-col items-center gap-4 py-12 text-center">
                <span className="text-5xl">📭</span>
                <div>
                  <p className="font-semibold text-slate-700">No documents yet</p>
                  <p className="text-sm text-slate-500 mt-1">Generate or upload a document to get started.</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="secondary" size="sm" onClick={() => setTab('generate')}>Generate</Button>
                  <Button variant="ghost" size="sm" onClick={() => setTab('upload')}>Upload</Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Generated documents from D1 — tap to open viewer, pencil to edit */}
                {documents.map(doc => (
                  <div
                    key={doc.id}
                    className="card min-h-[108px] lg:min-h-[116px] flex items-center gap-4 text-left"
                  >
                    {/* Clickable area — open viewer */}
                    <button
                      onClick={() => handleOpenDoc(doc)}
                      className="flex items-center gap-4 flex-1 min-w-0 text-left hover:opacity-80 active:scale-[0.98] transition-all duration-150"
                      aria-label={`Open ${doc.title}`}
                    >
                      <span className="text-2xl flex-shrink-0">{doc.emoji || '📄'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-base truncate">{doc.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-slate-500">{doc.date}</span>
                          {doc.status?.includes('Attorney') && (
                            <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                              ⚠️ Review needed
                            </span>
                          )}
                        </div>
                      </div>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                        className="w-4 h-4 text-slate-300 flex-shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {/* Pencil edit button */}
                    <button
                      onClick={e => { e.stopPropagation(); setEditDoc(doc) }}
                      className="flex-shrink-0 text-slate-400 hover:text-brand-500 tap-target transition-colors ml-1"
                      aria-label={`Edit ${doc.title}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                ))}
                {/* Uploaded files from R2 — tap row to download, pencil to rename */}
                {files.map(f => {
                  const ext = f.filename?.split('.').pop()?.toLowerCase()
                  const icon = ext === 'pdf' ? '📄' : ['jpg','jpeg','png'].includes(ext) ? '🖼️' : '📁'
                  const size = f.size < 1024 * 1024
                    ? `${(f.size / 1024).toFixed(0)} KB`
                    : `${(f.size / (1024 * 1024)).toFixed(1)} MB`
                  return (
                    <div
                      key={f.id}
                      className="card min-h-[108px] lg:min-h-[116px] flex items-center gap-4 text-left"
                    >
                      {/* Clickable area — download */}
                      <button
                        onClick={() => handleDownloadFile(f)}
                        className="flex items-center gap-4 flex-1 min-w-0 text-left hover:opacity-80 active:scale-[0.98] transition-all duration-150"
                        aria-label={`Download ${f.filename}`}
                      >
                        <span className="text-2xl flex-shrink-0">{icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 text-base truncate">{f.filename}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-slate-500">{size}</span>
                            <span className="text-xs font-bold bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">Uploaded</span>
                          </div>
                        </div>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                          className="w-4 h-4 text-slate-500 flex-shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      {/* Pencil rename button */}
                      <button
                        onClick={(e) => openRename(e, f)}
                        className="flex-shrink-0 text-slate-400 hover:text-brand-500 tap-target transition-colors ml-1"
                        aria-label={`Rename ${f.filename}`}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* ── Generate modal ────────────────────────────────────────────────── */}
      {activeModal && (
        <GenerateModal
          template={activeModal}
          state={user?.state}
          onClose={() => { setActiveModal(null); setActiveNotes('') }}
          onGenerate={handleGenerated}
          initialNotes={activeNotes}
        />
      )}

      {/* ── Saved doc viewer (opened from My Docs) ───────────────────────── */}
      {viewDoc && (
        <DocumentViewer
          template={viewDoc.template}
          docText={viewDoc.docText}
          onClose={() => setViewDoc(null)}
        />
      )}

      {/* ── Edit document modal ───────────────────────────────────────────── */}
      {editDoc && (
        <EditDocModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSave={(id, updates) => updateDocument(id, updates)}
        />
      )}

      {/* ── Rename file modal ────────────────────────────────────────────── */}
      {renameTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end lg:items-center lg:justify-center z-50"
          onClick={closeRename}
          role="dialog"
          aria-modal="true"
          aria-label="Rename file"
        >
          <div
            className="w-full max-w-lg mx-auto bg-white rounded-t-3xl lg:rounded-3xl p-6 flex flex-col gap-4 animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="w-12 h-1 rounded-full bg-slate-200 mx-auto -mt-2 mb-2" />

            <h3 className="font-bold text-slate-800 text-base">Rename File</h3>

            <input
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitRename() }}
              placeholder="Enter new filename"
              className="input-base text-base"
              autoFocus
            />

            {renameError && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                {renameError}
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="ghost" size="md" className="flex-1" onClick={closeRename} disabled={renameLoading}>
                Cancel
              </Button>
              <Button variant="primary" size="md" className="flex-1" loading={renameLoading} onClick={submitRename}>
                {renameLoading ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
